import { groupBy, throttle } from "lodash";
import axios from "axios";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { AppContext } from "../AppContext";
import { isDraftTableOrder } from "./draftOrderUtils";
import { useCustomers } from "./hooks/useCustomer";
import { useGetDocTypeListShort } from "./hooks/useDocTypesData";
import { useAddons, useItemGroups, useItems } from "./hooks/useItemsData";
import { useGetTableOrders } from "./hooks/useOrdersData";
import { useSearchPOSOpening } from "./hooks/usePOSData";
import { useGetUserDefaults } from "./hooks/useUser";

export const PosContext = createContext();

export const PosProvider = ({ children }) => {
    const {
        isKitchenUser,
        company,
        isTabsPos,
        multipleCompanies,
        socket,
        menuAllowedGroups,
        userInfo,
        posProfile,
        posProfiles,
        setPosProfile,
    } = useContext(AppContext);

    // Always filter by company when we have one, regardless of multi-company mode
    const tableOrdersQueryArgs = {
        company: company?.name,
        posProfile: posProfile?.name,
        disabled: !company?.name,
    };

    // NOTE: keep the call order stable, only the params change.
    const {
        data: tableOrders,
        isFetched,
        refetch: rawRefetchTablesData,
    } = useGetTableOrders(tableOrdersQueryArgs);

    // Throttled refetch with promise fan-out to preserve callers using .then()
    const throttledRefetchRef = useRef(null);
    const pendingResolversRef = useRef([]);

    const refetchTablesData = useCallback(() => {
        return new Promise((resolve, reject) => {
            pendingResolversRef.current.push({ resolve, reject });
            if (throttledRefetchRef.current) {
                throttledRefetchRef.current();
            }
        });
    }, []);

    useEffect(() => {
        throttledRefetchRef.current = throttle(
            () => {
                const settleAll = (fn) => {
                    const pending = pendingResolversRef.current.splice(0);
                    pending.forEach(({ resolve, reject, value, error }) =>
                        fn({ resolve, reject })
                    );
                };
                rawRefetchTablesData()
                    .then((result) => {
                        const pending = pendingResolversRef.current.splice(0);
                        pending.forEach(({ resolve }) => resolve(result));
                    })
                    .catch((error) => {
                        const pending = pendingResolversRef.current.splice(0);
                        pending.forEach(({ reject }) => reject(error));
                    });
            },
            1000,
            { leading: true, trailing: true }
        );

        return () => {
            if (
                throttledRefetchRef.current &&
                throttledRefetchRef.current.cancel
            ) {
                throttledRefetchRef.current.cancel();
            }
            // Reject any pending promises on unmount
            if (pendingResolversRef.current.length > 0) {
                const pending = pendingResolversRef.current.splice(0);
                pending.forEach(({ reject }) =>
                    reject(new Error("PosContext unmounted"))
                );
            }
        };
    }, [rawRefetchTablesData]);

    const { data: getDefaultRoom, refetch: refetchDefaultRoom } =
        useGetUserDefaults("Room");

    const {
        data: menuItems,
        isFetched: isMenuItemsFetched,
        refetch: refetchMenuItems,
    } = useItems({ allowedGroups: menuAllowedGroups });

    useEffect(() => {
        refetchMenuItems();
    }, []);

    const { data: addonsItems } = useAddons();
    const { data: groupedItems } = useItemGroups({
        allowedGroups: menuAllowedGroups,
    });

    const { data: customers, refetch: refetchCustomers } = useCustomers();

    const { data: todayOpen } = useSearchPOSOpening({ user: userInfo.name });
    const [todayPosName, setTodayPosName] = useState(null);
    const { data: posEntryDetails } = useSearchPOSOpening({
        user: userInfo.name,
    });

    const [addresses, setAddresses] = useState(null);
    const [territoryOptions, setTerritoryOptions] = useState([""]);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const { data: getAddresses, refetch: refetchAddresses } =
        useGetDocTypeListShort("Address", ["*"]);
    const { data: getTerritoryOptions } = useGetDocTypeListShort("Territory");

    const [rooms, setRooms] = useState({});
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [posProfileCompanyMismatch, setPosProfileCompanyMismatch] =
        useState(null);

    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);

    const [takeawayTable, setTakeawayTable] = useState(null);

    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);

    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const discountEnabled = true;

    const [isAppLoading, setAppLoading] = useState(true);

    useEffect(() => {
        if (getAddresses) {
            setAddresses(
                groupBy(getAddresses, ({ address_title }) => address_title)
            );
        }
    }, [getAddresses]);

    useEffect(() => {
        if (getTerritoryOptions) {
            const tmpTerritory = [...getTerritoryOptions];
            setTerritoryOptions(tmpTerritory.map((i) => i.name));
        }
    }, [getTerritoryOptions]);

    useEffect(() => {
        if (todayOpen) {
            if (todayOpen.length > 0) {
                setTodayPosName(todayOpen[0].name);
            } else {
                setTodayPosName(null);
            }
        }
    }, [todayOpen]);

    useEffect(() => {
        if (selectedOrder) {
            setOrderItems(selectedOrder.entry_items || []);
        } else {
            setOrderItems([]);
        }
    }, [selectedOrder]);

    useEffect(() => {
        if (tableOrders) {
            const roomsData = tableOrders.message?.rooms || {};
            setRooms(roomsData);
            setTakeawayTable(tableOrders.message?.takeaway || null);

            const prevRoomName = selectedRoom?.name;

            // Room preference order:
            // 1) keep current selected room (if still exists)
            // 2) user's default room (if configured and exists)
            // 3) first room in available rooms list
            const keepRoom =
                prevRoomName &&
                Object.values(roomsData).find((r) => r?.name === prevRoomName);

            const defaultRoomName =
                getDefaultRoom?.message && getDefaultRoom.message.length > 0
                    ? getDefaultRoom.message[0]
                    : null;
            const defaultRoom =
                defaultRoomName &&
                Object.values(roomsData).find((r) => r?.name === defaultRoomName);

            const firstRoom = Object.values(roomsData)[0];

            const nextRoom = keepRoom || defaultRoom || firstRoom || null;
            // Always refresh selected room object reference so tables/orders update without changing the selected room.
            setSelectedRoom(nextRoom);
        }
    }, [tableOrders, getDefaultRoom, selectedRoom]);

    useEffect(() => {
        const roomCompany = selectedRoom?.company || null;
        if (!roomCompany) {
            setPosProfileCompanyMismatch(null);
            return;
        }

        if (posProfile?.company === roomCompany) {
            setPosProfileCompanyMismatch(null);
            return;
        }

        const sameCompanyProfile = (posProfiles || []).find(
            (profile) => profile.company === roomCompany
        );

        if (sameCompanyProfile) {
            if (sameCompanyProfile.name !== posProfile?.name) {
                setPosProfile(sameCompanyProfile.name, true);
            }
            setPosProfileCompanyMismatch(null);
            return;
        }

        setPosProfileCompanyMismatch({
            roomCompany,
            message: `No POS Profile found for company "${roomCompany}". Please create/select a matching POS Profile.`,
        });
    }, [
        selectedRoom?.name,
        selectedRoom?.company,
        posProfile?.name,
        posProfile?.company,
        posProfiles,
        setPosProfile,
    ]);

    useEffect(() => {
        if (selectedRoom && selectedRoom.tables) {
            setTables(Object.values(selectedRoom.tables));
        } else {
            setTables([]);
        }
    }, [selectedRoom]);

    useEffect(() => {
        if (selectedRoom && isMenuItemsFetched) {
            setAppLoading(false);
        }
    }, [isMenuItemsFetched, selectedRoom]);

    useEffect(() => {
        if (orders && orders.length > 0) {
        } else {
            setSelectedOrder(null);
        }
    }, [orders]);

    const getMenuItem = (item) => {
        if (menuItems) {
            return menuItems.find((i) => i.item_code === item);
        } else {
            return null;
        }
    };

    const changeSelectedTable = (tableName, orderName) => {
        if (isTabsPos) {
            setSelectedTable(tableOrders?.message.takeaway);
            setOrders(
                Object.values(tableOrders?.message?.takeaway?.orders || {})
            );
        } else {
            if (tableName && tables.length > 0) {
                let newTable = {};
                if (tableName === "takeaway") {
                    newTable = takeawayTable; //-TODO CHANGE TO USER PREFRENCES
                } else {
                    newTable = tables.find(
                        (i) => i.tableDescription === tableName
                    );
                }

                setSelectedTable(newTable);
                if (orderName) {
                    setSelectedOrder(newTable.orders[orderName]);
                } else if (newTable && newTable.orders) {
                    setSelectedOrder(Object.values(newTable.orders)[0]);
                }

                if (newTable && newTable.orders) {
                    setOrders(Object.values(newTable.orders));
                }
            } else {
                setOrders(null);
            }
        }
    };

    const setTakeawayOrder = (orderName) => {
        if (orderName) {
            if (takeawayTable) {
                const takeawayOrder = takeawayTable.orders[orderName];
                setSelectedTable(takeawayTable);
                if (takeawayOrder) {
                    setSelectedOrder(takeawayOrder);
                } else {
                    // Draft orders are not included in floor data (filtered).
                    // Detect which doctype to fetch from based on the name prefix.
                    const doctype = isDraftTableOrder(orderName)
                        ? "Draft Table Order"
                        : "Table Order";
                    axios
                        .get("method/frappe.client.get", {
                            params: { doctype, name: orderName },
                        })
                        .then(({ data }) => {
                            if (data?.message) {
                                setSelectedOrder(data.message);
                                setOrders((prev) => {
                                    const list = Array.isArray(prev) ? prev : [];
                                    const exists = list.some(
                                        (o) => o?.name === data.message.name
                                    );
                                    return exists ? list : [data.message, ...list];
                                });
                            } else {
                                setSelectedOrder(null);
                            }
                        })
                        .catch(() => {
                            setSelectedOrder(null);
                        });
                }
            }
        } else {
            //new takeaway table
            setSelectedTable(takeawayTable);
            setSelectedOrder(null);
        }
    };

    useEffect(() => {
        if (!socket) return;

        socket.emit("doctype_subscribe", "Table Order");

        const onListUpdate = (data) => {
            if (data?.doctype === "Table Order") {
                // Throttled in refetchTablesData to prevent floods.
                refetchTablesData();
            }
        };

        socket.on("list_update", onListUpdate);

        return () => {
            // Prevent accumulating listeners which can amplify refetching and cause visible "hard refresh" flicker.
            if (socket.off) {
                socket.off("list_update", onListUpdate);
            } else if (socket.removeListener) {
                socket.removeListener("list_update", onListUpdate);
            }
        };
    }, [socket, refetchTablesData]);

    return (
        <PosContext.Provider
            value={{
                tables,
                selectedTable,
                setSelectedTable,
                changeSelectedTable,
                orders,
                setOrders,
                tableOrders,
                isAppLoading,
                selectedOrder,
                setSelectedOrder,
                orderItems,
                menuItems,
                getMenuItem,
                customerContext: [selectedCustomer, setSelectedCustomer],
                setSelectedCustomer,
                selectedRoom,
                takeawayTable,
                customers,
                refetchCustomers,
                setTakeawayOrder,
                addonsItems,
                groupedItems,
                roomContext: [selectedRoom, setSelectedRoom],
                roomOptions: rooms,
                todayOpen,
                posEntryDetails,
                refetchTablesData,
                addressContext: [selectedAddress, setSelectedAddress],
                addresses,
                refetchAddresses,
                territoryOptions,
                refetchDefaultRoom,
                discountEnabled,
                socket,
                posProfileCompanyMismatch,
            }}
        >
            {isKitchenUser ? (
                children
            ) : isFetched ? (
                children
            ) : (
                <div>Loading</div>
            )}
            {/* {children} */}
        </PosContext.Provider>
    );
};
